import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withLocalStorage, withStorageSync } from '@ngrx-toolkit/core';

import { MAX_DAYS_OF_HISTORY } from '@/app/app/constants/history.const';
import { UserSettings } from '@/app/app/interfaces/api/user-settings';
import { SettingsApi } from '@/app/app/services/api/settings.api';
import { DateExtension } from '@/app/app/services/extensions/date.extension';
import { storagePrefix } from '@/shared/lib/constants/storage.const';
import { applyMergePatch, createMergePatch } from '@/shared/lib/core/utils/merge-patch.util';
import { UserStore } from '@/shared/lib/stores/user.store';

type SettingsState = {
  settings: UserSettings;
};

const initialState: SettingsState = {
  settings: {},
};

// User-level app settings (layouts, page filters, auto refresh), not tenant bound. Kept on the device for a fast start
// and offline use, and synced with /users/settings (see doc/user-settings-api.md).
export const SettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    fontSize: computed(() => store.settings().display?.fontSize ?? 'normal'),
    historyAutoRefresh: computed(() => store.settings().history?.autoRefresh ?? false),
    historyCompactLayout: computed(() => store.settings().history?.compactLayout ?? false),
    monitoringCompactLayout: computed(() => store.settings().monitoringResults?.compactLayout ?? false),
  })),
  withMethods((store, settingsApi = inject(SettingsApi), dateExtension = inject(DateExtension), userStore = inject(UserStore)) => {
    const push = (patch: UserSettings) => {
      // A failed save keeps the local value; the next load sends it again
      settingsApi.patchSettings(patch).subscribe({ error: () => undefined });
    };

    // History start date at most MAX_DAYS_OF_HISTORY days ago; an end date before it becomes "up to today"
    const historyDateCorrection = (settings: UserSettings): UserSettings | null => {
      const minDate = dateExtension.addDaysToTodayAsDateString(-MAX_DAYS_OF_HISTORY) ?? '';
      const startDate = settings.history?.startDate;
      if (!startDate || startDate >= minDate) {
        return null;
      }
      const endDate = settings.history?.endDate;
      return { history: { startDate: minDate, ...(endDate && endDate < minDate ? { endDate: null } : {}) } };
    };

    const update = (patch: UserSettings) => {
      patchState(store, { settings: applyMergePatch(store.settings(), patch) });
      push(patch);
    };

    // The compact layouts used to be stored on the device only (UserStore); taken over once when still unset
    const legacyLocalSettings = (current: UserSettings): UserSettings => {
      const patch: UserSettings = {};
      const history = userStore.compactHistoryLayout();
      const monitoring = userStore.compactMonitoringLayout();
      if (current.history?.compactLayout === undefined && typeof history === 'boolean') {
        patch.history = { compactLayout: history };
      }
      if (current.monitoringResults?.compactLayout === undefined && typeof monitoring === 'boolean') {
        patch.monitoringResults = { compactLayout: monitoring };
      }
      return patch;
    };

    const withCorrections = (settings: UserSettings): UserSettings => {
      let result = applyMergePatch(settings, legacyLocalSettings(settings));
      userStore.clearLegacyLayoutSettings();
      // A fiber filter that is empty (or "All fibers", 0) means no filter: no entry is kept for it
      const emptyFiberFilters = Object.entries(result.monitoringResults?.fiberFilter ?? {})
        .filter(([, fibers]) => !fibers || fibers.length === 0 || fibers.includes(0));
      if (emptyFiberFilters.length > 0) {
        result = applyMergePatch(result, { monitoringResults: { fiberFilter: Object.fromEntries(emptyFiberFilters.map(([id]) => [id, null])) } });
      }
      const dateCorrection = historyDateCorrection(result);
      if (dateCorrection) {
        result = applyMergePatch(result, dateCorrection);
      }
      return result;
    };

    return {
      // After login: the server wins; values only known locally (set offline or before the API existed) are kept and sent
      load() {
        settingsApi.getSettings().subscribe({
          next: (response) => {
            const server = response?.settings ?? {};
            const merged = withCorrections(applyMergePatch(store.settings(), server));
            patchState(store, { settings: merged });
            // Send only what differs from the server, including null for removed keys (e.g. a corrected end date)
            const patch = createMergePatch(server, merged) as UserSettings;
            if (Object.keys(patch).length > 0) {
              push(patch);
            }
          },
          // API not reachable: keep working with the local settings
          error: () => patchState(store, { settings: withCorrections(store.settings()) }),
        });
      },
      update,
      // "Auto refresh history" used to be the tenant preference autoOpenLastScanReport; taken over once when unset
      adoptLegacyAutoRefresh(autoOpenLastScanReport: boolean | null | undefined) {
        if (store.settings().history?.autoRefresh === undefined && typeof autoOpenLastScanReport === 'boolean') {
          update({ history: { autoRefresh: autoOpenLastScanReport } });
        }
      },
      clear() {
        patchState(store, initialState);
      },
    };
  }),
  withStorageSync(`${storagePrefix}_settings`, withLocalStorage()),
);
